import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { electionsAdminAPI } from '../api/elections-admin';

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function toDateInput(date) {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function ElectionCard({ election }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    name: election.name,
    electionDate: toDateInput(election.electionDate),
    electionType: election.electionType,
    registrationDeadline: toDateInput(election.registrationDeadline),
    earlyVotingStart: toDateInput(election.earlyVotingStart),
    earlyVotingEnd: toDateInput(election.earlyVotingEnd),
    absenteeBallotDeadline: toDateInput(election.absenteeBallotDeadline),
    description: election.description || '',
  });

  const updateMutation = useMutation({
    mutationFn: () => electionsAdminAPI.updateElection(election.id, draft),
    onSuccess: () => {
      setEditing(false);
    },
  });

  const handleSave = () => {
    updateMutation.mutate();
  };

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <div
        className="bg-neutral-50 px-4 py-3 cursor-pointer hover:bg-neutral-100 flex items-center justify-between"
        onClick={() => !editing && setExpanded(!expanded)}
      >
        <div className="flex-1">
          <h3 className="font-semibold text-neutral-900">{election.name}</h3>
          <p className="text-xs text-neutral-500 mt-1">
            {fmtDate(election.electionDate)}
            {election.registrationDeadline && ` · Reg. ${fmtDate(election.registrationDeadline)}`}
            {election.earlyVotingStart && ` · Early voting ${fmtDate(election.earlyVotingStart)}`}
          </p>
        </div>
        <svg
          className={`w-5 h-5 text-neutral-400 transition ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 14l-7 7m0 0l-7-7m7 7V3"
          />
        </svg>
      </div>

      {expanded && (
        <div className="border-t border-neutral-100 p-4 space-y-4 bg-white">
          {!editing ? (
            <>
              <button
                onClick={() => setEditing(true)}
                className="text-xs px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Edit election
              </button>

              {/* Voting hours section */}
              <ElectionVotingHours election={election} />
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="text-xs font-medium text-neutral-700 block mb-1">
                    Election name
                  </label>
                  <input
                    type="text"
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    className="w-full px-3 py-1.5 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-neutral-700 block mb-1">
                      Election date
                    </label>
                    <input
                      type="date"
                      value={draft.electionDate}
                      onChange={(e) => setDraft({ ...draft, electionDate: e.target.value })}
                      className="w-full px-3 py-1.5 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-700 block mb-1">Type</label>
                    <input
                      type="text"
                      value={draft.electionType}
                      onChange={(e) => setDraft({ ...draft, electionType: e.target.value })}
                      placeholder="General, Primary, etc."
                      className="w-full px-3 py-1.5 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-neutral-700 block mb-1">
                      Registration deadline
                    </label>
                    <input
                      type="date"
                      value={draft.registrationDeadline}
                      onChange={(e) => setDraft({ ...draft, registrationDeadline: e.target.value })}
                      className="w-full px-3 py-1.5 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-700 block mb-1">
                      Absentee deadline
                    </label>
                    <input
                      type="date"
                      value={draft.absenteeBallotDeadline}
                      onChange={(e) =>
                        setDraft({ ...draft, absenteeBallotDeadline: e.target.value })
                      }
                      className="w-full px-3 py-1.5 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-neutral-700 block mb-1">
                      Early voting start
                    </label>
                    <input
                      type="date"
                      value={draft.earlyVotingStart}
                      onChange={(e) => setDraft({ ...draft, earlyVotingStart: e.target.value })}
                      className="w-full px-3 py-1.5 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-700 block mb-1">
                      Early voting end
                    </label>
                    <input
                      type="date"
                      value={draft.earlyVotingEnd}
                      onChange={(e) => setDraft({ ...draft, earlyVotingEnd: e.target.value })}
                      className="w-full px-3 py-1.5 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-neutral-700 block mb-1">
                    Description
                  </label>
                  <textarea
                    value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    placeholder="Optional election description..."
                    className="w-full px-3 py-1.5 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows="2"
                  />
                </div>
              </div>

              {updateMutation.error && (
                <p className="text-xs text-red-600">{updateMutation.error.message}</p>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  className="px-3 py-1 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {updateMutation.isPending ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    setDraft({
                      name: election.name,
                      electionDate: toDateInput(election.electionDate),
                      electionType: election.electionType,
                      registrationDeadline: toDateInput(election.registrationDeadline),
                      earlyVotingStart: toDateInput(election.earlyVotingStart),
                      earlyVotingEnd: toDateInput(election.earlyVotingEnd),
                      absenteeBallotDeadline: toDateInput(election.absenteeBallotDeadline),
                      description: election.description || '',
                    });
                  }}
                  className="px-3 py-1 text-xs bg-neutral-100 text-neutral-600 rounded-lg hover:bg-neutral-200"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ElectionVotingHours({ election }) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({
    scheduleType: 'early_voting_in_person',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    timeZone: 'America/Chicago',
    isAllDay: false,
    sourceUrl: '',
    label: '',
  });

  const createMutation = useMutation({
    mutationFn: (data) => electionsAdminAPI.createVotingHours(election.id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['authority-detail'] });
      setShowForm(false);
      setDraft({
        scheduleType: 'early_voting_in_person',
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        timeZone: 'America/Chicago',
        isAllDay: false,
        sourceUrl: '',
        label: '',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (windowId) => electionsAdminAPI.deleteVotingHours(election.id, windowId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['authority-detail'] });
    },
  });

  const scheduleWindows = election.scheduleWindows || [];

  return (
    <div className="space-y-3 pt-3 border-t border-neutral-100">
      <h4 className="text-sm font-semibold text-neutral-900">Voting hours</h4>

      {scheduleWindows.map((window) => (
        <div key={window.id} className="bg-neutral-50 p-3 rounded-lg space-y-1 text-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-medium text-neutral-900">{window.scheduleType}</p>
              <p className="text-xs text-neutral-600">
                {fmtDate(window.startDate)} – {fmtDate(window.endDate)}
                {window.startTime && ` · ${window.startTime} – ${window.endTime}`}
              </p>
            </div>
            <button
              onClick={() => deleteMutation.mutate(window.id)}
              disabled={deleteMutation.isPending}
              className="text-xs px-2 py-0.5 text-red-600 hover:text-red-700 disabled:opacity-50"
            >
              Delete
            </button>
          </div>
          {window.sourceUrl && (
            <a
              href={window.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary-600 hover:text-primary-700 block truncate"
            >
              Source: {window.sourceUrl}
            </a>
          )}
        </div>
      ))}

      {!showForm ? (
        <button
          onClick={() => setShowForm(true)}
          className="text-xs px-3 py-1 bg-neutral-100 text-neutral-600 rounded-lg hover:bg-neutral-200"
        >
          + Add voting hours
        </button>
      ) : (
        <div className="bg-neutral-50 p-3 rounded-lg space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium block mb-0.5">Schedule type</label>
              <select
                value={draft.scheduleType}
                onChange={(e) => setDraft({ ...draft, scheduleType: e.target.value })}
                className="w-full px-2 py-1 text-xs border border-neutral-300 rounded"
              >
                <option value="early_voting_in_person">Early voting</option>
                <option value="election_day">Election day</option>
                <option value="dropbox">Dropbox</option>
                <option value="office_hours">Office hours</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium block mb-0.5">All day?</label>
              <input
                type="checkbox"
                checked={draft.isAllDay}
                onChange={(e) => setDraft({ ...draft, isAllDay: e.target.checked })}
                className="w-4 h-4"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium block mb-0.5">Start date</label>
              <input
                type="date"
                value={draft.startDate}
                onChange={(e) => setDraft({ ...draft, startDate: e.target.value })}
                className="w-full px-2 py-1 text-xs border border-neutral-300 rounded"
              />
            </div>
            <div>
              <label className="text-xs font-medium block mb-0.5">End date</label>
              <input
                type="date"
                value={draft.endDate}
                onChange={(e) => setDraft({ ...draft, endDate: e.target.value })}
                className="w-full px-2 py-1 text-xs border border-neutral-300 rounded"
              />
            </div>
          </div>

          {!draft.isAllDay && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium block mb-0.5">Start time</label>
                <input
                  type="time"
                  value={draft.startTime}
                  onChange={(e) => setDraft({ ...draft, startTime: e.target.value })}
                  className="w-full px-2 py-1 text-xs border border-neutral-300 rounded"
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-0.5">End time</label>
                <input
                  type="time"
                  value={draft.endTime}
                  onChange={(e) => setDraft({ ...draft, endTime: e.target.value })}
                  className="w-full px-2 py-1 text-xs border border-neutral-300 rounded"
                />
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-medium block mb-0.5">Source URL</label>
            <input
              type="url"
              value={draft.sourceUrl}
              onChange={(e) => setDraft({ ...draft, sourceUrl: e.target.value })}
              placeholder="https://..."
              className="w-full px-2 py-1 text-xs border border-neutral-300 rounded"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={() => {
                if (draft.startDate && draft.endDate) {
                  createMutation.mutate({
                    scheduleType: draft.scheduleType,
                    label: draft.label || null,
                    startDate: draft.startDate,
                    endDate: draft.endDate,
                    startTime: draft.startTime || null,
                    endTime: draft.endTime || null,
                    timeZone: draft.timeZone,
                    isAllDay: draft.isAllDay,
                    sourceUrl: draft.sourceUrl || null,
                  });
                }
              }}
              disabled={createMutation.isPending}
              className="px-2 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Saving…' : 'Add'}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-2 py-1 text-xs bg-neutral-200 text-neutral-600 rounded hover:bg-neutral-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminElectionEditor({ scraperData }) {
  const elections = scraperData?.elections || [];

  return (
    <div className="space-y-4">
      {elections.length === 0 ? (
        <p className="text-sm text-neutral-500 italic">No elections found.</p>
      ) : (
        elections.map((election) => <ElectionCard key={election.id} election={election} />)
      )}
    </div>
  );
}
